import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import { 
  fetchFlowByIntentKey,
  deleteFlow,
  updateFlow,
  clearError,
  clearSuccess
} from '../store/slices/visualFlowSlice'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import CreateFlowModal from '../components/(admin)/CreateFlowModal'
import NotificationModal from '../components/NotificationModal'
import { resolveMediaPublicUrl } from '../utils/mediaUrl'

const VisualFlowDetails = () => {
  const dispatch = useDispatch()
  const { intentKey } = useParams()
  const navigate = useNavigate()
  const { currentFlow, loading, error, success, saving } = useSelector((state) => state.visualFlow)
  
  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  
  // Notification modal state
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  })

  useEffect(() => {
    if (intentKey) {
      dispatch(fetchFlowByIntentKey(intentKey))
    }
  }, [intentKey, dispatch])

  const handleSignOut = async () => {
    try {
      await dispatch(signOut()).unwrap()
      navigate('/signin')
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  // Show notifications for errors and success
  useEffect(() => {
    if (error) {
      showNotification('error', 'Error', error)
      dispatch(clearError())
    }
    if (success) {
      showNotification('success', 'Success', success)
      dispatch(clearSuccess())
      if (intentKey) {
        dispatch(fetchFlowByIntentKey(intentKey)) // Refresh flow
      }
    }
  }, [error, success, dispatch, intentKey])

  // Helper functions for notifications
  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  const handleDelete = () => {
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!currentFlow) return

    setIsDeleting(true)
    dispatch(deleteFlow(currentFlow._id)).then(() => {
      setIsDeleteModalOpen(false)
      setIsDeleting(false)
      // Navigate back after a short delay to show the success message
      setTimeout(() => {
        navigate('/visual-guides')
      }, 1500)
    }).catch(() => {
      setIsDeleting(false)
    })
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false)
  }

  const handleEdit = () => {
    setIsEditModalOpen(true)
  }

  const handleEditClose = () => {
    if (saving) return
    setIsEditModalOpen(false)
  }

  const handleEditSave = async (updatedFlowData) => {
    if (!currentFlow) return

    // Transform the form data into the correct format for the API
    const apiData = {
      intentKey: updatedFlowData.intentKey || updatedFlowData.linkedIntents,
      title: updatedFlowData.title,
      description: updatedFlowData.description,
      steps: []
    }
    
    // Process files/steps
    if (updatedFlowData.files && updatedFlowData.files.length > 0) {
      updatedFlowData.files.forEach((file, index) => {
        const step = {
          title: file.name || `Step ${index + 1}`,
          description: file.description || ''
        }
        if (file.file && file.file instanceof File) {
          step.media = file.file
        } else if (file.media_asset_id) {
          step.media_asset_id = file.media_asset_id
        }
        apiData.steps.push(step)
      })
    }
    
    try {
      await dispatch(updateFlow({ flowId: currentFlow._id, flowData: apiData })).unwrap()
      setIsEditModalOpen(false)
    } catch (_) {
      // Error toast via Redux; keep edit modal open
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    })
  }

  const containerStyle = {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: colors.background.primary
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '40px',
    backgroundColor: colors.background.primary,
    width: 'calc(100vw - 280px)'
  }

  const breadcrumbStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: '32px'
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const actionButtonsStyle = {
    display: 'flex',
    gap: '12px'
  }

  const editButtonStyle = {
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }

  const deleteButtonStyle = {
    backgroundColor: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }

  const flowInfoStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '32px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  }

  const flowTitleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: '16px'
  }

  const flowMetaStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  }

  const metaItemStyle = {
    fontSize: '14px',
    color: colors.text.secondary
  }

  const metaLabelStyle = {
    fontWeight: '600',
    color: colors.text.primary
  }

  const tableContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  }

  const headerRowStyle = {
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB'
  }

  const headerCellStyle = {
    padding: '16px 24px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: colors.text.primary,
    borderBottom: '1px solid #E5E7EB'
  }

  const rowStyle = {
    borderBottom: '1px solid #F3F4F6'
  }

  const cellStyle = {
    padding: '16px 24px',
    fontSize: '14px',
    color: colors.text.primary
  }

  const stepNumberStyle = {
    fontWeight: '600',
    color: colors.primary
  }

  const stepTitleStyle = {
    fontWeight: '600',
    color: colors.text.primary
  }

  const intentStyle = {
    color: colors.primary,
    textDecoration: 'underline',
    cursor: 'pointer'
  }

  const mediaThumbnailStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '4px',
    objectFit: 'cover',
    backgroundColor: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: '#6B7280',
    position: 'relative',
    overflow: 'hidden'
  }

  // Delete modal styles (matching other components)
  const deleteModalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001
  }

  const deleteModalStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: '12px',
    padding: '32px',
    width: '400px',
    maxWidth: '90vw',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }

  const deleteModalTitleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: '0 0 16px 0'
  }

  const deleteModalTextStyle = {
    fontSize: '16px',
    color: colors.text.secondary,
    margin: '0 0 24px 0',
    lineHeight: '1.5'
  }

  const deleteModalButtonGroupStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  }

  const deleteCancelButtonStyle = {
    backgroundColor: 'white',
    color: colors.text.primary,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const getDeleteConfirmButtonStyle = () => ({
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    opacity: isDeleting ? 0.7 : 1
  })

  if (loading) {
    return (
      <div style={containerStyle}>
        <Sidebar />
        <div style={mainContentStyle}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading flow details...</div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !currentFlow) {
    return (
      <div style={containerStyle}>
        <Sidebar />
        <div style={mainContentStyle}>
          <div style={{ textAlign: 'center', padding: '40px', color: '#EF4444' }}>
            <div>Error: {error || 'Flow not found'}</div>
            <button 
              onClick={() => navigate('/visual-guides')}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Back to Visual Guides
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <Sidebar />
      <div style={mainContentStyle}>
        <PageTopBar
          title={`Visual Guide Manager > ${currentFlow?.title || ''}`}
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <div style={actionButtonsStyle}>
            <button style={editButtonStyle} onClick={handleEdit}>
              Edit
            </button>
            <button style={deleteButtonStyle} onClick={handleDelete}>
              Delete Flow
            </button>
          </div>
        </div>

        {/* Flow Information */}
        <div style={flowInfoStyle}>
          <div style={flowTitleStyle}>{currentFlow.title}</div>
          <div style={flowMetaStyle}>
            <div style={metaItemStyle}>
              <span style={metaLabelStyle}>Intent Key:</span> {currentFlow.intent_key}
            </div>
            <div style={metaItemStyle}>
              <span style={metaLabelStyle}>Description:</span> {currentFlow.description || 'No description'}
            </div>
            <div style={metaItemStyle}>
              <span style={metaLabelStyle}>Created:</span> {formatDate(currentFlow.created_at)}
            </div>
            <div style={metaItemStyle}>
              <span style={metaLabelStyle}>Last Updated:</span> {formatDate(currentFlow.updated_at)}
            </div>
          </div>
        </div>

        {/* Steps Table */}
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead style={headerRowStyle}>
              <tr>
                <th style={headerCellStyle}>Sr no.</th>
                <th style={headerCellStyle}>Title</th>
                <th style={headerCellStyle}>Linked Intent</th>
                <th style={headerCellStyle}>Attached Media</th>
                <th style={headerCellStyle}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {currentFlow.steps && currentFlow.steps.length > 0 ? (
                currentFlow.steps.map((step, index) => {
                  console.log('Step data:', step)
                  console.log('Media asset ID:', step.media_asset_id)
                  return (
                    <tr key={step._id || index} style={rowStyle}>
                      <td style={cellStyle}>
                        <span style={stepNumberStyle}>{step.order || index + 1}</span>
                      </td>
                      <td style={cellStyle}>
                        <span style={stepTitleStyle}>{step.title}</span>
                      </td>
                      <td style={cellStyle}>
                        <span style={intentStyle}>
                          {currentFlow.intent_key || 'No Intent Linked'}
                        </span>
                      </td>
                      <td style={cellStyle}>
                        {step.media_asset_id && step.media_asset_id !== null ? (
                          <a 
                            href={resolveMediaPublicUrl(step.media_asset_id._id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                          >
                            <div style={mediaThumbnailStyle}>
                              {step.media_asset_id.mime_type?.startsWith('image/') ? (
                                <img 
                                  src={resolveMediaPublicUrl(step.media_asset_id._id)}
                                  alt={step.media_asset_id.original_name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '4px'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.nextSibling.style.display = 'flex'
                                  }}
                                />
                              ) : null}
                              <div 
                                style={{
                                  display: step.media_asset_id.mime_type?.startsWith('image/') ? 'none' : 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  height: '100%',
                                  fontSize: '12px',
                                  color: '#6B7280'
                                }}
                              >
                                📄
                              </div>
                              <div style={{
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px',
                                backgroundColor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                fontSize: '8px',
                                padding: '1px 3px',
                                borderRadius: '2px'
                              }}>
                                {step.media_asset_id.original_name?.split('.').pop()?.toUpperCase() || 'FILE'}
                              </div>
                            </div>
                          </a>
                        ) : (
                          <div style={mediaThumbnailStyle}>
                            No Media
                          </div>
                        )}
                      </td>
                      <td style={cellStyle}>
                        {step.description || '-'}
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan="5" style={{ ...cellStyle, textAlign: 'center', color: '#6B7280' }}>
                    No steps found for this flow.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && currentFlow && (
        <div style={deleteModalOverlayStyle}>
          <div style={deleteModalStyle}>
            <h2 style={deleteModalTitleStyle}>Delete Flow</h2>
            <p style={deleteModalTextStyle}>
                    Are you sure you want to delete <strong>"{currentFlow.title}"</strong>?
              This action cannot be undone and will remove all associated data.
            </p>
            <div style={deleteModalButtonGroupStyle}>
              <button 
                style={deleteCancelButtonStyle} 
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                style={getDeleteConfirmButtonStyle()} 
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Flow Modal */}
      <CreateFlowModal
        isOpen={isEditModalOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
        isEditMode={true}
        existingFlow={currentFlow}
        isSaving={saving}
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  )
}

export default VisualFlowDetails
